<script setup lang="ts">
import useBreakpointsLimit from "~/composables/use-breakpoints-limit";
import usePagination from "~/composables/use-pagination";
import { EVENT_STATUS } from "~/constants";
import EventsAPI from "~/services/events";
import getErrorLocalePathMessage from "~/utils/locale";

export interface BaseEventSliderProps {
  type: "upcoming" | "live";
  limit?: number;
  title?: string;
  hasControls?: boolean;
}

const props = withDefaults(defineProps<BaseEventSliderProps>(), {
  hasControls: true,
});

const { t, locale: currentLocale, te } = useI18n();
const useLimit = useBreakpointsLimit({ sm: 2, md: 4, lg: 6, xl: 8, xl2: 10, xl3: 20, xl4: 20, xl5: 20, xl6: 20 });

const staticLimit = 20;
const dynamicLimit = useLimit.value;

const limit = props.limit ? props.limit : staticLimit;

const { items: slides, loadMore, error, canLoadMore } = usePagination<IModelEvent>({
  immediate: true,
  pageSize: limit,
  loadItemsFn: (page: number, pageSize: number) => {
    const params: Record<string, string | undefined | TSportType[]> = {};
    params.type = props.type;

    return EventsAPI.load({ page, pageSize, ...params });
  },
});

const vMotionInitialX = ref(0);
const activeGroupIndex = ref(0);
const slidesPerPage = Math.floor(dynamicLimit / 2);

const slidesGrouped = computed(() => {
  const grouped = [];
  for (let i = 0; i < slides.value.length; i += slidesPerPage)
    grouped.push(slides.value.slice(i, i + slidesPerPage));

  return grouped;
});
const seeAllLink = computed(() => props.type === EVENT_STATUS.LIVE ? "/live/all/" : "/upcoming/");
const hasMultipleSlidesGrouped = computed(() => slidesGrouped?.value.length > 1);
const hasControls = computed(() => props.hasControls && hasMultipleSlidesGrouped.value);
const isLastGroup = computed(() => activeGroupIndex.value === slidesGrouped?.value.length - 1);
const isFirstGroup = computed(() => activeGroupIndex.value === 0);
const isRtl = computed(() => currentLocale.value === "he");
const errorMessage = computed(() => error.value && getErrorLocalePathMessage(error.value, { t, te }).message);

function moveSlides(groupsToMove: number) {
  vMotionInitialX.value = groupsToMove * -10000;

  const nextGroup = activeGroupIndex.value + groupsToMove;

  if (nextGroup >= slidesGrouped.value.length)
    activeGroupIndex.value = 0;
  else if (nextGroup < 0)
    activeGroupIndex.value = slidesGrouped.value.length - 1;
  else
    activeGroupIndex.value = nextGroup;

  if (canLoadMore.value && isLastGroup.value)
    loadMore();
}
</script>

<template>
  <div v-if="errorMessage">
    <BaseNotFound :text="errorMessage" />
  </div>
  <div v-else-if="slides.length" class="main-container">
    <div class="flex items-center" :class="props.title ? 'justify-between' : 'justify-end'">
      <h3 v-if="props.title" class="font-bold text-main mb-6 text-2xl">
        {{ props.title }}
      </h3>

      <div v-if="hasControls" class="flex gap-3 ">
        <BaseButton
          class="eventSlider__button"
          type="icon"
          name="eventSlider-slide-back"
          :title="t('button.slide_back')"
          :disabled="isFirstGroup"
          @click="moveSlides(-1)"
        >
          <IconChevronRight v-if="isRtl" class="relative z-10" :width="10" :height="10" />
          <IconChevronLeft v-else class="relative z-10" :width="10" :height="10" />
        </BaseButton>
        <BaseButton
          class="eventSlider__button"
          type="icon"
          name="eventSlider-slide-forward"
          :title="t('button.slide_forward')"
          :disabled="isLastGroup"
          @click="moveSlides(1)"
        >
          <IconChevronLeft v-if="isRtl" class="relative z-10" :width="10" :height="10" />
          <IconChevronRight v-else class="relative z-10" :width="10" :height="10" />
        </BaseButton>
      </div>
    </div>
    <template v-for="(slideGroup, groupIndex) in slidesGrouped">
      <div
        v-if="activeGroupIndex === groupIndex" :key="groupIndex"
        v-motion
        :initial="{ x: vMotionInitialX }"
        :enter="{ x: 0 }"
        class="grid gap-6 grid-cols-1 sm:grid-cols-2 2xl:grid-cols-5 3xl:grid-cols-6 4xl:grid-cols-8 5xl:grid-cols-10" :class="[
          props.type !== 'upcoming' ? 'xl:grid-cols-4' : '',
        ]"
      >
        <div v-for="(item, itemIndex) in slideGroup" :key="itemIndex">
          <BaseButton
            v-if="props.type === EVENT_STATUS.LIVE"
            tag="nuxt-link"
            type="icon"
            class="card-item-link"
            :name="`item-live-${item.id}`"
            :to="{ path: `/events/${item.id}/` }"
          >
            <CardLive
              v-bind="item"
              :title="item.metaTitle"
              :statistic-tooltip-title="t('labels.statistics')"
              :sport-type-tooltip-title="item.sportType ? t(`sport_types.${item.sportType}`) : ''"
            />
          </BaseButton>
          <BaseButton
            v-else-if="props.type === EVENT_STATUS.UPCOMING"
            tag="nuxt-link"
            type="icon"
            class="card-item-link"
            :name="`item-upcoming-${item.id}`"
            :to="{ path: `/events/${item.id}/` }"
          >
            <CardUpcoming
              v-bind="item"
              :title="item.metaTitle"
              :sport-type-tooltip-title="item.sportType ? t(`sport_types.${item.sportType}`) : ''"
            />
          </BaseButton>
        </div>
      </div>
    </template>
    <div v-if="hasMultipleSlidesGrouped" class="text-center mt-6">
      <BaseButton name="see-all-items" class="text-heading-4" tag="nuxt-link" :to="seeAllLink" link>
        {{ t('labels.see_all') }}
      </BaseButton>
    </div>
    <div class="hr mt-10" role="separator" />
  </div>
</template>

<style lang="scss">
  .eventSlider__button {
    @apply w-8 h-8
    rounded-full overflow-hidden
    flex items-center justify-center
    transition duration-100
    hover:shadow-lg;

    color: #fff;
    transform: translateY(-50%);

    &::before {
      @apply absolute w-full h-full z-0 opacity-75 rounded-full;

      content: '';
      background-color: var(--base-eventSlider-controls-bgColor);
    }
  }
</style>
